import { useRef } from 'react'

import { Box, Section, Text } from '@factorialco/deprecated-design-system'
import {
  RichTextDisplay,
  RichTextEditor,
  RichTextEditorHandle,
} from '@factorialco/factorial-one-react/dist/experimental'
import {
  AtsMessageTemplateBodySectionFormFragment,
  AtsUpdateMessageTemplateBodyDocument,
} from 'generated/resources/graphql'

import ErrorBoundary from 'modules/core/components/ErrorBoundary'
import InformationTooltip from 'modules/core/components/Inputs/InformationTooltip'
import TextEditor, { type EditorHandle } from 'modules/core/components/TextEditor'
import TextField from 'modules/core/components/TextField'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import useRichTextEditor from 'modules/core/hooks/useRichTextEditor'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { AtsMessageVariablesSelector } from 'modules/ats/components/AtsMessageVariablesSelector'

interface EditTemplateBodyFormValues {
  body: string | undefined | null
}

interface Props {
  messageTemplate: AtsMessageTemplateBodySectionFormFragment
  canEdit: boolean
}

export const BodySectionForm = ({ messageTemplate, canEdit }: Props) => {
  const { canUseRichTextEditor, loading: richTextEditorLoading, editorConfig } = useRichTextEditor()

  const editorRef = useRef<EditorHandle>(null)
  const richTextEditorRef = useRef<RichTextEditorHandle>(null)
  const [updateAtsMessageTemplateBody, { loading }] = useMutation(
    AtsUpdateMessageTemplateBodyDocument,
    {
      extractorFn: (data) => data.atsMutations.updateAtsMessageTemplate,
    }
  )
  const { id: messageTemplateId, body } = messageTemplate

  const { values, setFieldValue, dirty, handleSubmit, resetForm, isValid } =
    useForm<EditTemplateBodyFormValues>({
      defaultValues: {
        body: body || '',
      },
      validate(data) {
        const formErrors: FormErrors<EditTemplateBodyFormValues> = {}
        if (dirty && (!data.body || data.body === '')) {
          formErrors.body = i18n.t('common.empty')
        }
        return formErrors
      },
      async onSubmit(data, { setErrors }) {
        const result = await updateAtsMessageTemplateBody({
          variables: {
            id: messageTemplateId,
            body: data.body,
          },
        })
        const handled = handleResult(result, (resultErrors) => {
          setErrors(resultErrors)
        })

        if (handled.success) {
          app.setFlash({
            type: 'correct',
            message: i18n.t('common.saved_successfully'),
          })

          resetForm({ values })
        }

        return handled
      },
    })

  const addVariable = (variable: string) => {
    setFieldValue('body', `${values.body} ${variable}`)
    richTextEditorRef?.current?.setContent(`${values.body ?? ''} ${variable}`)
  }

  return (
    <Section.Form
      illustration="send-email-envelope"
      title={i18n.t('ats.manage_message_templates.template_form.body_title')}
      description={i18n.t('ats.manage_message_templates.template_form.body_description')}
      submitLabel={i18n.t('common.apply')}
      onSubmit={handleSubmit}
      isDirty={dirty}
      disabled={loading || !isValid}
    >
      <TextField
        label={i18n.t('ats.manage_message_templates.template_form.subject_label')}
        value={i18n.t('ats.manage_message_templates.template_form.subject_placeholder')}
        secondaryLabel={
          <InformationTooltip
            information={i18n.t('ats.manage_message_templates.template_form.subject_tooltip')}
            icon="question"
          />
        }
        readonly
      />
      {canEdit ? (
        <ErrorBoundary>
          <Box gap="s4">
            <Box flexDirection="row" justifyContent="spaceBetween">
              <Text.Normal color="grey1000" size="200" weight="medium">
                {i18n.t('ats.manage_message_templates.template_form.body_label')}
              </Text.Normal>
              <AtsMessageVariablesSelector onSelect={addVariable} showJobData showSenderData />
            </Box>
            {richTextEditorLoading ? (
              <RichTextEditor.Skeleton />
            ) : canUseRichTextEditor ? (
              <RichTextEditor
                ref={richTextEditorRef}
                {...editorConfig}
                title={i18n.t('ats.manage_message_templates.template_form.body_label')}
                onChange={(data) => setFieldValue('body', data?.value)}
                placeholder={i18n.t('ats.manage_message_templates.template_form.body_placeholder')}
                initialEditorState={{
                  content: values.body ?? '',
                }}
                maxCharacters={10000}
              />
            ) : (
              <TextEditor
                ref={editorRef}
                onChange={(value) => setFieldValue('body', value)}
                placeholder={i18n.t('ats.manage_message_templates.template_form.body_placeholder')}
                initialEditorState={{
                  data: values.body,
                }}
                height="s400"
              />
            )}
          </Box>
        </ErrorBoundary>
      ) : (
        <Box
          background="grey200"
          border={{
            all: {
              width: 's2',
              style: 'solid',
              color: 'grey400',
            },
          }}
          borderRadius={{ all: 'abs008' }}
          padding="s16"
        >
          {values.body && <RichTextDisplay content={values.body} />}
        </Box>
      )}
    </Section.Form>
  )
}
